import React from 'react';
import {NavLink, useNavigate, useParams} from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ScreenshotIcon from '@mui/icons-material/Screenshot';
import RefreshIcon from '@mui/icons-material/Sync';
import {
  Breadcrumbs,
  Button,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {apiCall, formatDate, getRole, YesNoDialog} from '../../utils';

import {NewRevisionDialog} from './NewRevisionDialog';

import {Project, ProjectRevisionInfo} from '@bumpy/i18n_common';

export const ProjectInfo: React.FC = () => {
  const role = getRole();
  const navigate = useNavigate();
  const {projectUuid} = useParams();
  const [project, setProject] = React.useState<Project | undefined>(undefined);
  const [deleteRevision, setDeleteRevision] = React.useState<
    ProjectRevisionInfo | undefined
  >(undefined);
  const [newRevision, setNewRevision] = React.useState(false);

  const refresh = () => {
    apiCall({
      method: 'GET',
      url: `/projects/${projectUuid}`,
      secure: true,
    }).then((response) => setProject(response.data));
  };
  React.useEffect(refresh, [projectUuid]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={5}>
        <Breadcrumbs aria-label="breadcrumb">
          <Breadcrumbs aria-label="breadcrumb">
            <NavLink to="/">Projects</NavLink>
            <Typography color="secondary">{project?.name ?? ''}</Typography>
          </Breadcrumbs>
        </Breadcrumbs>
      </Grid>
      <Grid item xs={7}>
        <Stack direction="row" gap={1} justifyContent="flex-end">
          {role === 'admin' && (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setNewRevision(true);
                }}
                startIcon={<AddIcon />}
              >
                Revision
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  navigate('edit');
                }}
                startIcon={<EditIcon />}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  navigate('screenshots');
                }}
                startIcon={<ScreenshotIcon />}
              >
                Screenshots
              </Button>
            </>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={refresh}
            startIcon={<RefreshIcon />}
          >
            Refresh
          </Button>
        </Stack>
      </Grid>
      {project && (
        <Grid item xs={12}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Last modified</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {project.revisions!.map((revision) => (
                  <TableRow key={revision.uuid}>
                    <TableCell>{revision.name}</TableCell>
                    <TableCell>{formatDate(revision.modifiedAt)}</TableCell>
                    <TableCell>
                      {role === 'admin' && (
                        <IconButton
                          onClick={() => {
                            setDeleteRevision(revision);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={() => {
                          navigate(`revisions/${revision.uuid}`);
                        }}
                      >
                        <ArrowForwardIosIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}
      {deleteRevision && (
        <YesNoDialog
          title="Are you sure?"
          message={`Do you want to delete revision "${deleteRevision.name}"?`}
          onYes={() => {
            apiCall({
              method: 'DELETE',
              url: `/projects/${projectUuid}/revisions/${deleteRevision!.uuid}`,
              secure: true,
            }).then(refresh);
            setDeleteRevision(undefined);
          }}
          onNo={() => {
            setDeleteRevision(undefined);
          }}
        />
      )}
      {newRevision && (
        <NewRevisionDialog
          project={project!}
          onClose={() => {
            setNewRevision(false);
            refresh();
          }}
        />
      )}
    </Grid>
  );
};
